/*
 * SolidForms — Instant Slicer (Path A)
 *
 * Everything runs in the visitor's browser: parse the STL, measure it, estimate
 * filament and time, draw it. Nothing is uploaded until they ask for a quote,
 * so the section costs nothing per visitor and answers instantly.
 *
 * The maths is the browser port of the WhatsApp bot's src/slice.js Path A,
 * with one upgrade: it sums triangle surface area, so shell + infill drives the
 * weight instead of a fixed factor. That is what makes the infill and layer
 * controls mean something.
 *
 * NEVER renders a price. Grams and time only — the team quotes.
 *
 * Upgrades every [data-sf-slicer] on the page, and re-upgrades on
 * shopify:section:load so the theme editor stays live.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- config */

  var BUILD_VOLUME = [800, 800, 1000];   // mm — matches SLICE_BUILD_VOLUME
  var MAX_BYTES = 40 * 1024 * 1024;      // per file
  var MAX_FILES = 10;                    // per order, matches the bot
  var MAX_PHOTOS = 6;                    // reference images, not printed
  var MIN_WALL = 2;                      // mm — the KB's stated minimum

  // The worker's standard profile, so browser numbers land near the real slice.
  var PROFILE = {
    perimeters: 3,
    lineWidth: 0.42,
    speed: 180,          // mm/s
    layerOverheadS: 1.4, // per-layer non-extruding time
    flowCap: 24,         // mm³/s ceiling
    layerHeight: 0.2,    // the shop standard — no longer a customer choice
    infill: 0.15         // fallback when no strength control is present
  };

  /*
   * The strength slider. Four stops, because "how strong does it need to be"
   * has about four honest answers — and a percentage means nothing to someone
   * ordering a part. The infill each one means is ours to tune; change these
   * numbers and every quote follows.
   */
  var STRENGTH = [
    { label: 'Light', key: 'st_light', infill: 10 },
    { label: 'Medium', key: 'st_medium', infill: 25 },
    { label: 'Strong', key: 'st_strong', infill: 50 },
    { label: 'Very strong', key: 'st_vstrong', infill: 100 },
  ];
  var STRENGTH_DEFAULT = 1; // Medium

  /*
   * Colours offered per part. Two or three shades of each major family — enough
   * that someone can say what they want, few enough to read at a glance. These
   * are generic names, not spool SKUs: the team matches the closest filament
   * they have in stock, which is what happens in practice anyway.
   */
  var COLOURS = [
    { name: 'White', hex: '#F4F4EF' },
    { name: 'Beige', hex: '#E4D7BF' },
    { name: 'Grey', hex: '#9AA0A6' },
    { name: 'Black', hex: '#1B1B1B' },
    { name: 'Red', hex: '#C62828' },
    { name: 'Dark red', hex: '#7E1D1D' },
    { name: 'Pink', hex: '#E86A9A' },
    { name: 'Orange', hex: '#F26722' },
    { name: 'Yellow', hex: '#F5C518' },
    { name: 'Gold', hex: '#C9A227' },
    { name: 'Light green', hex: '#7CB342' },
    { name: 'Green', hex: '#2E7D32' },
    { name: 'Teal', hex: '#00897B' },
    { name: 'Sky blue', hex: '#5BA7E8' },
    { name: 'Blue', hex: '#1E6FD9' },
    { name: 'Navy', hex: '#1B2A5B' },
    { name: 'Lilac', hex: '#B79CE0' },
    { name: 'Purple', hex: '#7B4BC9' },
    { name: 'Brown', hex: '#6D4C33' },
    { name: 'Silver', hex: '#C0C4C8' },
  ];

  /* ------------------------------------------------------- localisation
   * Strings the script writes into the DOM. The page hands us a map built
   * from the theme locale files; anything missing falls back to the English
   * literal, so the tool still reads correctly if the map never arrives.
   * Values sent to the team (WhatsApp payload, order notes) deliberately
   * stay in English so the bot and the shop floor read one vocabulary. */
  function T(key, fallback) {
    var m = window.SFSL_I18N;
    return (m && m[key]) || fallback;
  }
  function colourLabel(name) {
    if (!name) return name;
    return T('c_' + name.toLowerCase().replace(/[^a-z0-9]+/g, '_'), name);
  }
  function strengthLabel(step) {
    return T(STRENGTH[step].key, STRENGTH[step].label);
  }

  /* ------------------------------------------------------------ stl parsing */

  function isBinary(dv, len) {
    return len >= 84 && len === 84 + dv.getUint32(80, true) * 50;
  }

  function parseBinary(dv) {
    var count = dv.getUint32(80, true);
    if (count > 3000000) return null; // too heavy to hold in a tab
    var pos = new Float32Array(count * 9);
    var o = 84, k = 0, i, j;
    for (i = 0; i < count; i++) {
      var p = o + 12; // skip the normal, we recompute it
      for (j = 0; j < 9; j++) pos[k++] = dv.getFloat32(p + j * 4, true);
      o += 50;
    }
    return { count: count, pos: pos };
  }

  function parseAscii(buf) {
    var text = new TextDecoder('latin1').decode(new Uint8Array(buf));
    var re = /vertex\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)/g;
    var out = [], m;
    while ((m = re.exec(text))) {
      out.push(+m[1], +m[2], +m[3]);
      if (out.length > 9000000) break;
    }
    if (out.length < 9) return null;
    var count = Math.floor(out.length / 9);
    return { count: count, pos: new Float32Array(out.slice(0, count * 9)) };
  }

  function parseStl(buf) {
    var dv = new DataView(buf);
    return isBinary(dv, buf.byteLength) ? parseBinary(dv) : parseAscii(buf);
  }

  /* Signed tetrahedron volume x6 summed over every triangle == 6x mesh volume.
     Surface area comes along for free from the same cross product. */
  function measure(pos, count) {
    var vol6 = 0, area2 = 0, i, d;
    var min = [Infinity, Infinity, Infinity];
    var max = [-Infinity, -Infinity, -Infinity];
    for (i = 0; i < count; i++) {
      var o = i * 9;
      var ax = pos[o], ay = pos[o + 1], az = pos[o + 2];
      var bx = pos[o + 3], by = pos[o + 4], bz = pos[o + 5];
      var cx = pos[o + 6], cy = pos[o + 7], cz = pos[o + 8];
      vol6 += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
      var ux = bx - ax, uy = by - ay, uz = bz - az;
      var vx = cx - ax, vy = cy - ay, vz = cz - az;
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      area2 += Math.sqrt(nx * nx + ny * ny + nz * nz);
      var vs = [[ax, ay, az], [bx, by, bz], [cx, cy, cz]];
      for (var t = 0; t < 3; t++) {
        for (d = 0; d < 3; d++) {
          if (vs[t][d] < min[d]) min[d] = vs[t][d];
          if (vs[t][d] > max[d]) max[d] = vs[t][d];
        }
      }
    }
    return {
      dims: [max[0] - min[0], max[1] - min[1], max[2] - min[2]],
      min: min, max: max,
      volumeMm3: Math.abs(vol6) / 6,
      areaMm2: area2 / 2
    };
  }

  /* -------------------------------------------------------------- estimate */

  function estimate(meas, opts) {
    var s = opts.scale, s3 = s * s * s;
    var solid = meas.volumeMm3 * s3;
    var area = meas.areaMm2 * s * s;
    var height = meas.dims[2] * s;

    var wall = PROFILE.perimeters * PROFILE.lineWidth;
    var shell = Math.min(solid * 0.92, area * wall);
    var core = Math.max(0, solid - shell);
    var used = shell + core * opts.infill;

    var grams = (used / 1000) * opts.density;
    var sf = opts.speed || 1;
    var flow = Math.min(PROFILE.flowCap * sf, PROFILE.speed * sf * opts.layerH * PROFILE.lineWidth);
    var layers = Math.max(1, Math.ceil(height / opts.layerH));
    var seconds = used / flow + layers * PROFILE.layerOverheadS;

    /* The band runs upward from the geometry figure, never below it. This is
       geometry, not a slice, and everything a real slicer adds — supports,
       brims, extra perimeters, seams — adds material. So this figure is the
       floor, and the allowance is all on the upside. */
    return {
      grams: grams,
      gramsLo: grams * 1.2,
      gramsHi: grams * 1.7,
      minutes: seconds / 60,
      // Time tracks the material laid down, so it moves with the same band.
      minutesLo: (seconds / 60) * 1.2,
      minutesHi: (seconds / 60) * 1.7,
      dims: meas.dims.map(function (d) { return d * s; }),
      volumeCm3: solid / 1000
    };
  }

  function fitsBuildVolume(dims) {
    var d = dims.slice().sort(function (a, b) { return a - b; });
    var b = BUILD_VOLUME.slice().sort(function (a, b) { return a - b; });
    return d[0] <= b[0] && d[1] <= b[1] && d[2] <= b[2];
  }

  /* --------------------------------------------------------------- format */

  function fmtTime(min) {
    var m = Math.max(1, Math.round(min));
    if (m < 60) return m + ' min';
    var h = Math.floor(m / 60), r = m % 60;
    if (h < 24) return r ? h + 'h ' + (r < 10 ? '0' : '') + r : h + 'h';
    return Math.floor(h / 24) + 'd ' + (h % 24) + 'h';
  }
  function fmtMm(n) { return n >= 100 ? n.toFixed(0) : n.toFixed(1); }
  function fmtG(g) { return g < 10 ? g.toFixed(1) : String(Math.round(g)); }

  /* A short reference both sides can quote. Not secret, just a handle. */
  function makeRef() {
    return 'SF-' + Date.now().toString(36).slice(-4).toUpperCase() +
           Math.floor(Math.random() * 1296).toString(36).toUpperCase().padStart(2, '0');
  }

  /*
   * The WhatsApp message. One shape, used by both buttons, written so the
   * customer understands what they are sending and the team can act on it
   * without asking three questions back.
   *
   * Carries NO weight and NO time: the customer sees this text before they
   * send it, and those numbers are ours. The reference ties it to the full
   * detail that reaches the team by email.
   */
  function waTemplate(est, who) {
    var L = [];
    L.push('*SolidForms — custom print request*');
    L.push('Ref: ' + (est && est.ref ? est.ref : makeRef()));
    L.push('');
    if (who && who.name) L.push('Name: ' + who.name);
    if (who && who.email) L.push('Email: ' + who.email);
    if (who && (who.name || who.email)) L.push('');
    if (who && who.description) {
      L.push('*What I want made*');
      L.push(who.description);
      L.push('');
    }
    if (est) {
      var list = est.files || [];
      L.push(list.length > 1 ? '*The files*' : '*The file*');
      if (list.length > 1) {
        list.forEach(function (f, i) {
          L.push((i + 1) + '. ' + f.name + (f.quantity > 1 ? ' x' + f.quantity : '') +
                 ' — ' + f.dimsMm.join(' x ') + ' mm');
        });
      } else if (list.length === 1) {
        L.push('• Name: ' + list[0].name);
        L.push('• Size: ' + list[0].dimsMm.join(' x ') + ' mm');
      } else {
        L.push('• Name: ' + est.file);
      }
      L.push('• Material: ' + est.material);
      if (est.strength) L.push('• Strength: ' + est.strength);
      if (est.scalePct && est.scalePct !== 100) L.push('• Scaled to ' + est.scalePct + '%');
      if (!est.fitsBuildVolume) L.push('• Note: something is larger than one build — needs splitting');
      L.push('');
      L.push('_Sent through solidforms3d.com — you already have ' +
             (list.length > 1 ? 'the files' : 'the file') + '._');
    } else {
      L.push('_I do not have a 3D file yet._');
    }
    L.push('');
    L.push('Please confirm the price and how long it will take.');
    return L.join('\n');
  }

  // Shared with assets/sf-build-flow.js so both buttons speak the same language.
  window.SFSlicer = window.SFSlicer || {};
  window.SFSlicer.waTemplate = waTemplate;
  window.SFSlicer.makeRef = makeRef;

  /* -------------------------------------------------------------- viewport */

  /* ==================================================================
     THE IDLE MODEL
     ------------------------------------------------------------------
     The viewport used to sit empty behind the drop panel: a flat grid on
     a dark rectangle, which is what made the panel read as a placeholder
     rather than as a tool. It now holds a real mesh, lit and turning, so
     the first thing anyone sees is the thing their own file will become.

     Generated rather than downloaded — it costs no request, and it is
     tessellated finely enough that flat shading reads as a smooth
     surface instead of a handful of facets.
     ================================================================== */
  function helicalGear(opt) {
    var teeth = opt.teeth, rRoot = opt.root, rTip = opt.tip, rBore = opt.bore;
    var half = opt.height / 2, twist = opt.twist, layers = opt.layers;

    /* One tooth, as five points around the pitch circle: up the flank, across
       the tip, down the far flank, then two points holding the root. Repeated
       around, that is the whole outline. */
    var prof = [];
    var step = (Math.PI * 2) / teeth;
    for (var t = 0; t < teeth; t++) {
      var a = t * step;
      [[0.00, rRoot], [0.16, rTip], [0.44, rTip], [0.60, rRoot], [0.80, rRoot]]
        .forEach(function (pt) {
          var ang = a + pt[0] * step;
          prof.push([Math.cos(ang) * pt[1], Math.sin(ang) * pt[1]]);
        });
    }
    var n = prof.length;

    // The bore is a plain cylinder, sampled at the same count so the end caps
    // triangulate one-to-one against the outline.
    var bore = [];
    for (var i = 0; i < n; i++) {
      var b = (i / n) * Math.PI * 2;
      bore.push([Math.cos(b) * rBore, Math.sin(b) * rBore]);
    }

    var tris = [];
    function tri(a, b, c) { tris.push(a, b, c); }
    // Rotate a 2D point by the helix angle for a given height.
    function spin(pt, k) {
      var ang = twist * (k / layers - 0.5);
      var c = Math.cos(ang), s2 = Math.sin(ang);
      return [pt[0] * c - pt[1] * s2, pt[0] * s2 + pt[1] * c];
    }
    function at(list, i, k) {
      var p2 = spin(list[i % n], k);
      return [p2[0], p2[1], -half + (opt.height * k) / layers];
    }

    // Teeth: the outline swept up through the twist.
    for (var k = 0; k < layers; k++) {
      for (var i2 = 0; i2 < n; i2++) {
        var a1 = at(prof, i2, k), b1 = at(prof, i2 + 1, k);
        var c1 = at(prof, i2 + 1, k + 1), d1 = at(prof, i2, k + 1);
        tri(a1, b1, c1); tri(a1, c1, d1);
      }
    }
    // Bore: a straight cylinder through the middle.
    for (var k2 = 0; k2 < layers; k2++) {
      for (var i3 = 0; i3 < n; i3++) {
        var a2 = at(bore, i3, k2), b2 = at(bore, i3 + 1, k2);
        var c2 = at(bore, i3 + 1, k2 + 1), d2 = at(bore, i3, k2 + 1);
        tri(a2, c2, b2); tri(a2, d2, c2);
      }
    }
    // The two faces, as a ring between bore and outline.
    [0, layers].forEach(function (k3) {
      var up = k3 === layers;
      for (var i4 = 0; i4 < n; i4++) {
        var o1 = at(prof, i4, k3), o2 = at(prof, i4 + 1, k3);
        var i1 = at(bore, i4, k3), i2b = at(bore, i4 + 1, k3);
        if (up) { tri(i1, o1, o2); tri(i1, o2, i2b); }
        else    { tri(i1, o2, o1); tri(i1, i2b, o2); }
      }
    });

    var pos = new Float32Array(tris.length * 3);
    var min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (var v = 0; v < tris.length; v++) {
      for (var ax = 0; ax < 3; ax++) {
        var val = tris[v][ax];
        pos[v * 3 + ax] = val;
        if (val < min[ax]) min[ax] = val;
        if (val > max[ax]) max[ax] = val;
      }
    }
    return {
      pos: pos,
      count: tris.length / 3,
      meas: { min: min, max: max, dims: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] }
    };
  }

  var IDLE = null;
  function idleModel() {
    /*
     * A helical gear rather than a maths-y knot: it is the kind of thing this
     * shop actually prints, its faces are genuinely flat so the renderer's
     * flat shading is correct rather than a compromise, and the twist gives
     * it something to show while it turns.
     */
    if (!IDLE) {
      IDLE = helicalGear({
        teeth: 17, root: 34, tip: 47, bore: 12,
        height: 26, twist: 0.55, layers: 14
      });
    }
    return IDLE;
  }

  function Viewport(canvas) {
    var gl = canvas.getContext('webgl', { antialias: true, alpha: false });
    if (!gl) return null;

    var VS =
      'attribute vec3 aPos; attribute vec3 aNrm; uniform mat4 uMVP;' +
      'varying vec3 vN; varying float vZ;' +
      'void main(){ vN = aNrm; vZ = aPos.z; gl_Position = uMVP * vec4(aPos,1.0); }';
    var FS =
      'precision mediump float; varying vec3 vN; varying float vZ;' +
      'uniform vec3 uTop; uniform vec3 uBot; uniform vec3 uEdge;' +
      'uniform float uLine; uniform float uBand;' +
      'void main(){' +
      '  if (uLine > 0.5) { gl_FragColor = vec4(uEdge,1.0); return; }' +
      '  vec3 n = normalize(vN);' +
      '  vec3 c = mix(uBot, uTop, n.z*0.5+0.5);' +
      '  float rim = pow(1.0 - abs(n.y), 3.0) * 0.14;' +
      /* faint horizontal banding reads as layer lines without costing geometry */
      '  float band = smoothstep(0.985, 1.0, abs(fract(vZ*uBand)*2.0-1.0)) * 0.05;' +
      '  gl_FragColor = vec4(c + rim + band, 1.0);' +
      '}';

    function sh(type, src) {
      var o = gl.createShader(type);
      gl.shaderSource(o, src);
      gl.compileShader(o);
      return o;
    }
    var prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);

    var aPos = gl.getAttribLocation(prog, 'aPos');
    var aNrm = gl.getAttribLocation(prog, 'aNrm');
    function U(n) { return gl.getUniformLocation(prog, n); }
    var uMVP = U('uMVP'), uTop = U('uTop'), uBot = U('uBot'),
        uEdge = U('uEdge'), uLine = U('uLine'), uBand = U('uBand');

    var meshBuf = gl.createBuffer(), nrmBuf = gl.createBuffer(),
        gridBuf = gl.createBuffer(), gridNrm = gl.createBuffer();
    var meshCount = 0, gridCount = 0, radius = 100;
    var yaw = -0.7, pitch = 0.42, dist = 3.2, spin = true, dirty = true, alive = true;
    /* Idle means "this is decoration, not their model": it turns more slowly,
       sits further back, and never stops spinning when the pointer moves over
       it — nobody is inspecting a shape we invented. */
    var idle = false;
    var IDLE_TONE = [0.50, 0.56, 0.64];   // graphite; no copy to compete with now
    var userTone = [1, 0.42, 0.21];       // whatever the section configured
    var hot = [1, 0.42, 0.21];

    function mul(a, b) {
      var o = new Float32Array(16), i, j, k, s;
      for (i = 0; i < 4; i++) for (j = 0; j < 4; j++) {
        s = 0; for (k = 0; k < 4; k++) s += a[k * 4 + j] * b[i * 4 + k];
        o[i * 4 + j] = s;
      }
      return o;
    }
    function persp(fy, asp, n, f) {
      var t = 1 / Math.tan(fy / 2);
      return new Float32Array([t / asp, 0, 0, 0, 0, t, 0, 0, 0, 0, (f + n) / (n - f), -1, 0, 0, 2 * f * n / (n - f), 0]);
    }
    function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
    function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
    function unit(a) { var l = Math.sqrt(dot(a, a)) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }
    function lookAt(e, c, up) {
      var z = unit([e[0] - c[0], e[1] - c[1], e[2] - c[2]]);
      var x = unit(cross(up, z)), y = cross(z, x);
      return new Float32Array([x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0,
        -dot(x, e), -dot(y, e), -dot(z, e), 1]);
    }

    function faceNormals(pos, count) {
      var n = new Float32Array(count * 9), i, j;
      for (i = 0; i < count; i++) {
        var o = i * 9;
        var ux = pos[o + 3] - pos[o], uy = pos[o + 4] - pos[o + 1], uz = pos[o + 5] - pos[o + 2];
        var vx = pos[o + 6] - pos[o], vy = pos[o + 7] - pos[o + 1], vz = pos[o + 8] - pos[o + 2];
        var x = uy * vz - uz * vy, y = uz * vx - ux * vz, z = ux * vy - uy * vx;
        var l = Math.sqrt(x * x + y * y + z * z) || 1;
        x /= l; y /= l; z /= l;
        for (j = 0; j < 3; j++) { n[o + j * 3] = x; n[o + j * 3 + 1] = y; n[o + j * 3 + 2] = z; }
      }
      return n;
    }

    function buildGrid(ext) {
      var step = ext / 8, L = [], i;
      for (i = -8; i <= 8; i++) {
        var p = i * step;
        L.push(p, -ext, 0, p, ext, 0, -ext, p, 0, ext, p, 0);
      }
      var arr = new Float32Array(L);
      gridCount = arr.length / 3;
      gl.bindBuffer(gl.ARRAY_BUFFER, gridBuf);
      gl.bufferData(gl.ARRAY_BUFFER, arr, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, gridNrm);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(arr.length), gl.STATIC_DRAW);
    }

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(canvas.clientWidth * dpr), h = Math.round(canvas.clientHeight * dpr);
      if (w && h && (canvas.width !== w || canvas.height !== h)) {
        canvas.width = w; canvas.height = h; dirty = true;
      }
    }
    function bind(b, n) {
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, n);
      gl.enableVertexAttribArray(aNrm);
      gl.vertexAttribPointer(aNrm, 3, gl.FLOAT, false, 0, 0);
    }
    function readVar(name, fallback) {
      var v = getComputedStyle(canvas).getPropertyValue(name).trim();
      return v || fallback;
    }
    function toRgb(v) {
      if (v.indexOf('#') === 0) {
        var h = v.slice(1);
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return [0, 2, 4].map(function (i) { return parseInt(h.substr(i, 2), 16) / 255; });
      }
      // the design system stores colours as bare "r,g,b" triplets
      var p = v.split(',').map(function (n) { return parseFloat(n) / 255; });
      return p.length === 3 && p.every(function (n) { return n === n; }) ? p : [1, 0.42, 0.21];
    }

    function draw() {
      if (!alive) return;
      resize();
      if (spin && meshCount) { yaw += (idle ? 0.0022 : 0.0035); dirty = true; }
      if (dirty) {
        dirty = false;
        gl.viewport(0, 0, canvas.width, canvas.height);
        var bg = toRgb(readVar('--sfsl-stage', '#171c21'));
        gl.clearColor(bg[0], bg[1], bg[2], 1);
        gl.enable(gl.DEPTH_TEST);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        if (meshCount) {
          var d = radius * dist;
          var eye = [Math.cos(pitch) * Math.sin(yaw) * d, Math.cos(pitch) * Math.cos(yaw) * d,
                     Math.sin(pitch) * d + radius * 0.35];
          var mvp = mul(persp(0.9, canvas.width / canvas.height, radius * 0.05, radius * 40),
                        lookAt(eye, [0, 0, radius * 0.35], [0, 0, 1]));
          gl.useProgram(prog);
          gl.uniformMatrix4fv(uMVP, false, mvp);
          gl.uniform3f(uTop, hot[0] * 0.94 + 0.06, hot[1] * 0.74 + 0.08, hot[2] * 0.62 + 0.08);
          gl.uniform3f(uBot, 0.15, 0.17, 0.2);
          gl.uniform1f(uBand, 1 / Math.max(0.6, radius / 26));
          gl.uniform1f(uLine, 0);
          bind(meshBuf, nrmBuf);
          gl.drawArrays(gl.TRIANGLES, 0, meshCount);
          var g = toRgb(readVar('--sfsl-grid', '#39434c'));
          gl.uniform1f(uLine, 1);
          gl.uniform3f(uEdge, g[0], g[1], g[2]);
          bind(gridBuf, gridNrm);
          gl.drawArrays(gl.LINES, 0, gridCount);
        }
      }
      requestAnimationFrame(draw);
    }
    requestAnimationFrame(draw);

    /* orbit + zoom */
    var drag = null;
    canvas.addEventListener('pointerdown', function (e) {
      drag = { x: e.clientX, y: e.clientY };
      if (idle) return;
      spin = false;
      canvas.setPointerCapture(e.pointerId);
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!drag) return;
      yaw -= (e.clientX - drag.x) * 0.008;
      pitch = Math.max(-1.45, Math.min(1.45, pitch + (e.clientY - drag.y) * 0.008));
      drag = { x: e.clientX, y: e.clientY };
      dirty = true;
    });
    canvas.addEventListener('pointerup', function () { drag = null; });
    canvas.addEventListener('pointercancel', function () { drag = null; });
    canvas.addEventListener('wheel', function (e) {
      if (!meshCount) return;
      e.preventDefault();
      dist = Math.max(1.1, Math.min(9, dist * (1 + (e.deltaY > 0 ? 1 : -1) * 0.12)));
      dirty = true;
    }, { passive: false });
    window.addEventListener('resize', function () { dirty = true; });

    return {
      load: function (pos, count, meas) {
        var cx = (meas.min[0] + meas.max[0]) / 2;
        var cy = (meas.min[1] + meas.max[1]) / 2;
        var cz = meas.min[2]; // sit it on the plate
        var c = new Float32Array(pos.length);
        for (var i = 0; i < pos.length; i += 3) {
          c[i] = pos[i] - cx; c[i + 1] = pos[i + 1] - cy; c[i + 2] = pos[i + 2] - cz;
        }
        radius = Math.max(1, Math.sqrt(meas.dims[0] * meas.dims[0] + meas.dims[1] * meas.dims[1] +
                                       meas.dims[2] * meas.dims[2]) / 2);
        meshCount = count * 3;
        gl.bindBuffer(gl.ARRAY_BUFFER, meshBuf);
        gl.bufferData(gl.ARRAY_BUFFER, c, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, nrmBuf);
        gl.bufferData(gl.ARRAY_BUFFER, faceNormals(c, count), gl.STATIC_DRAW);
        buildGrid(radius * 1.9);
        this.reset();
      },
      reset: function () {
        // Idle sits closer and lower: the model is the backdrop of the panel,
        // so it should fill the frame rather than float in the middle of it.
        /* A three-quarter view for the idle pose, tilted enough to show the
           face and the depth of the teeth at once. The gear is wider than it
           is tall, so it reads around the button rather than behind it. */
        yaw = -0.7; pitch = idle ? 0.62 : 0.42; dist = idle ? 2.1 : 3.2;
        spin = true; dirty = true;
      },
      setIdle: function (v) {
        idle = !!v;
        hot = idle ? IDLE_TONE : userTone;
        this.reset();
      },
      clear: function () { meshCount = 0; dirty = true; },
      setHot: function (v) {
        userTone = toRgb(v);
        if (!idle) hot = userTone;
        dirty = true;
      },
      stopSpin: function () { spin = false; dirty = true; },
      destroy: function () { alive = false; }
    };
  }

  /* ------------------------------------------------------------- instance */

  function Slicer(root) {
    if (root.sfSlicer) return;
    var cfg = {};
    try { cfg = JSON.parse(root.getAttribute('data-sf-slicer') || '{}'); } catch (e) {}

    var q = function (sel) { return root.querySelector(sel); };

    /* Every uploaded part. `model` is whichever one the viewport is showing. */
    var models = [];
    var activeId = null;
    /* Whether the selected part's settings are open. Selecting the part you are
       already on closes them, which is the only way to get the list back to a
       glanceable height once a part has been opened. */
    var settingsOpen = false;
    var nextId = 1;
    var orderRef = null;
    var model = null;
    var view = null;
    var canvas = q('[data-sfsl-canvas]');
    if (canvas) view = Viewport(canvas);
    if (view && cfg.hot) view.setHot(cfg.hot);

    /* The empty viewport is the first thing anyone sees, so it holds a real
       turning mesh rather than an empty grid. Replaced the moment a file
       arrives, and brought back when the tool is emptied. */
    function showIdle() {
      if (!view) return;
      var m = idleModel();
      view.load(m.pos, m.count, m.meas);
      view.setIdle(true);
    }
    showIdle();

    var els = {
      drop: q('[data-sfsl-drop]'),
      input: q('[data-sfsl-input]'),
      browse: q('[data-sfsl-browse]'),
      stage: q('[data-sfsl-stage]'),
      tools: q('[data-sfsl-tools]'),
      reset: q('[data-sfsl-reset]'),
      clear: q('[data-sfsl-clear]'),
      hud: q('[data-sfsl-hud]'),
      tri: q('[data-sfsl-tri]'),
      fname: q('[data-sfsl-fname]'),
      files: q('[data-sfsl-files]'),
      filesList: q('[data-sfsl-files-list]'),
      filesCount: q('[data-sfsl-files-count]'),
      filesHint: q('[data-sfsl-files-hint]'),
      add: q('[data-sfsl-add]'),
      sizeLabel: q('[data-sfsl-size-label]'),
      matname: q('[data-sfsl-matname]'),
      matsub: q('[data-sfsl-matsub]'),
      dims: q('[data-sfsl-dims]'),
      vol: q('[data-sfsl-vol]'),
      fit: q('[data-sfsl-fit]'),
      fitTxt: q('[data-sfsl-fit-txt]'),
      results: q('[data-sfsl-results]'),
      wa: q('[data-sfsl-wa]'),
      summary: q('[data-sfsl-summary]')
    };
    var controls = {
      mat: q('[data-sfsl-mat]'),
      strength: q('[data-sfsl-strength]'),
      strengthV: q('[data-sfsl-strength-v]'),
      strengthTicks: q('[data-sfsl-strength-ticks]'),
      scale: q('[data-sfsl-scale]'),
      scaleV: q('[data-sfsl-scale-v]'),
      scaleReset: q('[data-sfsl-scale-reset]'),
      matDesc: q('[data-sfsl-mat-desc]'),
      partBar: q('[data-sfsl-part-bar]'),
      applyAll: q('[data-sfsl-apply-all]'),
      scope: null
    };

    var photos = [];
    var photoEls = {
      list: q('[data-sfsl-photos-list]'),
      input: q('[data-sfsl-photo-input]'),
      add: q('[data-sfsl-photo-add]'),
      note: q('[data-sfsl-photos-note]')
    };

    /* What the material dropdown is currently showing. */
    function readMaterial() {
      var el = controls.mat;
      var opt = el && el.options[el.selectedIndex];
      if (!opt) return { label: 'PLA+', density: 1.24, speed: 1, priceAs: 'PLA+' };
      return {
        label: opt.textContent.trim(),
        density: parseFloat(el.value) || 1.24,
        // How fast this material runs relative to PLA+.
        speed: parseFloat(opt.getAttribute('data-speed')) || 1,
        // "The team chooses for me" is quoted as PLA+, which is what we reach
        // for by default — so the customer still gets a real number.
        priceAs: opt.getAttribute('data-price-as') || opt.textContent.trim()
      };
    }

    function matOf(m) {
      return (m && m.mat) || { label: 'PLA+', density: 1.24, speed: 1, priceAs: 'PLA+' };
    }

    /** Everything that shapes an estimate now belongs to the part itself. */
    function settingsFor(m) {
      var mat = matOf(m);
      return {
        density: mat.density,
        matName: mat.label,
        speed: mat.speed || 1,
        layerH: PROFILE.layerHeight,
        infill: STRENGTH[stepIndex(m)].infill / 100,
        scale: (m && m.scale ? m.scale : 100) / 100
      };
    }

    function stepIndex(m) {
      var i = m && typeof m.strength === 'number' ? m.strength : STRENGTH_DEFAULT;
      return i >= 0 && i < STRENGTH.length ? i : STRENGTH_DEFAULT;
    }

    function strengthStep(m) {
      return STRENGTH[stepIndex(m || model)];
    }

    /** Point the shared sliders at whichever part is selected. */
    function syncControls() {
      if (!model) return;
      if (controls.strength) controls.strength.value = String(stepIndex(model));
      if (controls.scale) controls.scale.value = String(model.scale || 100);
      if (controls.mat) {
        var want = matOf(model).label;
        for (var oi = 0; oi < controls.mat.options.length; oi++) {
          if (controls.mat.options[oi].textContent.trim() === want) { controls.mat.selectedIndex = oi; break; }
        }
      }
    }

    function setText(el, v) { if (el) el.textContent = v; }

    /** Read the sliders back into the selected part. */
    function captureControls() {
      if (!model) return;
      if (controls.strength) model.strength = parseInt(controls.strength.value, 10);
      if (controls.scale) model.scale = parseInt(controls.scale.value, 10);
      if (controls.mat) model.mat = readMaterial();
    }

    function recompute() {
      var mScale = model ? (model.scale || 100) : 100;
      if (controls.scaleV && document.activeElement !== controls.scaleV) {
        controls.scaleV.value = String(mScale);
      }
      if (controls.scaleReset) controls.scaleReset.hidden = mScale === 100;

      /* What this material is actually good for. Desktop also gets it as a
         tooltip per option; this line is what a phone sees. */
      if (controls.matDesc && controls.mat) {
        var picked = controls.mat.options[controls.mat.selectedIndex];
        controls.matDesc.textContent = picked ? (picked.getAttribute('data-desc') || '') : '';
      }
      if (controls.strengthV) controls.strengthV.textContent = strengthLabel(stepIndex(model));
      if (controls.strengthTicks) {
        var on = stepIndex(model);
        var ticks = controls.strengthTicks.children;
        for (var t = 0; t < ticks.length; t++) ticks[t].classList.toggle('is-on', t === on);
      }
      if (!models.length) return;

      /* Every part is measured; the order is the sum. Weight and time are
         computed but deliberately NOT rendered — they are working numbers for
         the team, and the customer gets a price instead. */
      var files = [];
      var totals = { grams: 0, lo: 0, hi: 0, minutes: 0, pieces: 0, volume: 0 };
      var oversize = [];

      models.forEach(function (m) {
        var ms = settingsFor(m);
        var e = estimate(m.meas, ms);
        var qty = Math.max(1, m.qty || 1);
        var fits = fitsBuildVolume(e.dims);
        if (!fits) oversize.push(m.name);

        m.est = e;
        m.fits = fits;

        totals.grams += e.grams * qty;
        totals.lo += e.gramsLo * qty;
        totals.hi += e.gramsHi * qty;
        totals.minutes += e.minutes * qty;
        totals.volume += e.volumeCm3 * qty;
        totals.pieces += qty;

        files.push({
          name: m.name,
          quantity: qty,
          dimsMm: e.dims.map(function (d) { return +fmtMm(d); }),
          volumeCm3: +e.volumeCm3.toFixed(1),
          grams: +fmtG(e.grams),
          gramsLo: +fmtG(e.gramsLo),
          gramsHi: +fmtG(e.gramsHi),
          minutes: Math.round(e.minutes),
          minutesLo: Math.round(e.minutesLo),
          minutesHi: Math.round(e.minutesHi),
          material: matOf(m).label,
          materialPriceAs: matOf(m).priceAs,
          colour: m.colour || null,
          strength: STRENGTH[stepIndex(m)].label,
          infillPct: STRENGTH[stepIndex(m)].infill,
          scalePct: m.scale || 100,
          fits: fits
        });
      });

      refreshFiles();

      if (els.matname) {
        els.matname.classList.remove('sfsl__value--empty');
        /* One material for every part, or an honest "Mixed". The team-choice
           label is shortened here only — the full wording still travels with
           the order — because three wrapped lines swamp the readout on a
           phone. */
        var shown = uniform('material', files) || T('mixed', 'Mixed');
        els.matname.textContent = /team chooses/i.test(shown) ? T('team_choice', "Team's choice") : shown;
      }
      /* Under the material, say what it will be printed in — the one thing a
         customer still cannot see anywhere else on this panel. */
      var colours = files.map(function (f) { return f.colour; });
      var oneColour = uniform('colour', files);
      var named = colours.filter(Boolean).length;
      setText(
        els.matsub,
        named === 0
          ? T('colour_team', 'colour chosen by the team')
          : oneColour
            ? colourLabel(oneColour)
            : named + ' ' + T('n_of_m_colour', 'of') + ' ' + files.length + ' ' +
              T('parts_colour', 'parts have a colour')
      );

      /* One part: show its size. Several: show the count, because a combined
         bounding box would be a number about nothing. */
      var many = models.length > 1;
      setText(els.sizeLabel, many ? T('parts', 'Parts') : T('size', 'Size'));
      if (els.dims) {
        els.dims.classList.remove('sfsl__value--empty');
        els.dims.innerHTML = many
          ? models.length + '<span class="sfsl__unit">' + T('u_files', 'files') + '</span>' +
            (totals.pieces !== models.length
              ? ' ' + totals.pieces + '<span class="sfsl__unit">' + T('u_pieces', 'pieces') + '</span>'
              : '')
          : model.est.dims.map(fmtMm).join(' × ') + '<span class="sfsl__unit">mm</span>';
      }
      setText(els.vol, many
        ? totals.volume.toFixed(1) + ' cm³ ' + T('vol_total', 'of material in total')
        : model.count.toLocaleString() + ' ' + T('triangles', 'triangles'));

      var thin = !many && Math.min.apply(null, model.est.dims) < MIN_WALL;
      if (els.fit) {
        els.fit.hidden = false;
        els.fit.className = 'sfsl__status ' + (oversize.length ? 'sfsl__status--warn' : 'sfsl__status--ok');
      }
      if (els.fitTxt) {
        els.fitTxt.innerHTML = oversize.length
          ? '<strong>' + (oversize.length === 1
              ? oversize[0] + ' ' + T('is_bigger', 'is')
              : oversize.length + ' ' + T('are_bigger', 'parts are')) +
            ' ' + T('oversize_body', 'bigger than one build.') + '</strong> ' +
            T('oversize_note', 'Past 800 × 800 × 1000 mm we print in sections and bond them. Send it over and the team will plan the split.')
          : (thin
            ? '<strong>' + T('fits', 'Fits our machines') + '.</strong> ' +
              T('thin_note_a', 'Its thinnest dimension is under') + ' ' + MIN_WALL + ' ' +
              T('thin_note_b', 'mm, so the team will check it prints reliably at this size.')
            : '<strong>' + (many ? T('all_fit', 'All parts fit') : T('fits', 'Fits our machines')) +
              '</strong> — ' + T('fits_upto', 'up to 800 × 800 × 1000 mm each.'));
      }

      if (els.results) els.results.hidden = false;

      // Anything hosting the tool (the guided Build With Us flow, say) listens
      // for this rather than reaching into the DOM for the numbers.
      // gramsLo/gramsHi is a band on purpose — it is a geometry guess, and the
      // team should see honest bounds, not false precision. It runs upward from
      // the estimate: a real slice adds material, it does not save any.
      var payload = {
        ref: orderRef,
        file: files.length === 1 ? files[0].name : files.length + ' files',
        files: files,
        fileCount: files.length,
        pieces: totals.pieces,
        material: uniform('material', files) || 'Mixed',
        density: models.length ? matOf(models[0]).density : 1.24,
        // Order-wide values only mean something when every part agrees; the
        // per-file entries above are the truth either way.
        infillPct: uniform('infillPct', files),
        strength: uniform('strength', files) || 'Mixed',
        scalePct: uniform('scalePct', files) || 100,
        dimsMm: files.length === 1 ? files[0].dimsMm : null,
        volumeCm3: +totals.volume.toFixed(1),
        gramsEach: +fmtG(totals.grams),
        gramsLo: Math.max(1, Math.round(totals.lo)),
        gramsHi: Math.max(2, Math.round(totals.hi)),
        minutesEach: Math.round(totals.minutes),
        fitsBuildVolume: oversize.length === 0,
        source: 'in-browser geometry estimate'
      };

      // The customer reads this message before sending it, so it carries no
      // weights or times — just what the request is, plus a reference the team
      // can match against the emailed detail.
      if (els.wa && cfg.wa) {
        els.wa.href = 'https://wa.me/' + String(cfg.wa).replace(/\D/g, '') +
          '?text=' + encodeURIComponent(waTemplate(payload, {}));
      }

      if (els.summary) els.summary.value = JSON.stringify(payload);
      root.dispatchEvent(new CustomEvent('sfsl:estimate', {
        detail: payload, bubbles: true
      }));
      schedulePrices(files);
    }

    /* --------------------------------------------------------- photos */

    function renderPhotos() {
      if (!photoEls.list) return;
      photoEls.list.innerHTML = photos.map(function (p) {
        return (
          '<li class="sfsl__photo">' +
            '<img src="' + p.url + '" alt="">' +
            '<button type="button" data-photo-remove="' + p.id + '" aria-label="Remove ' +
              esc(p.name) + '">&times;</button>' +
          '</li>'
        );
      }).join('');
      if (photoEls.note) {
        photoEls.note.textContent = photos.length
          ? photos.length + ' ' + (photos.length === 1
              ? T('photo_attached', 'photo attached')
              : T('photos_attached', 'photos attached'))
          : 'A sketch, the part it has to fit, or how you want it to look.';
      }
    }

    function addPhotos(list) {
      var incoming = Array.prototype.slice.call(list || []);
      for (var i = 0; i < incoming.length; i++) {
        var f = incoming[i];
        if (photos.length >= MAX_PHOTOS) break;
        if (!/^image\//.test(f.type)) continue;
        if (f.size > MAX_BYTES) continue;
        photos.push({ id: nextId++, name: f.name, file: f, url: URL.createObjectURL(f) });
      }
      renderPhotos();
    }

    function removePhoto(id) {
      photos = photos.filter(function (p) {
        if (p.id === id) URL.revokeObjectURL(p.url);
        return p.id !== id;
      });
      renderPhotos();
    }

    /** The shared value of a field, or null when the parts disagree. */
    function uniform(key, list) {
      if (!list.length) return null;
      var first = list[0][key];
      return list.every(function (f) { return f[key] === first; }) ? first : null;
    }

    /* ---------------------------------------------------------- colours */

    var colourEls = {
      wrap: q('[data-sfsl-colours]'),
      list: q('[data-sfsl-swatches]'),
      value: q('[data-sfsl-colour-v]')
    };

    function renderColours() {
      if (!colourEls.wrap || !colourEls.list) return;
      if (!model) { colourEls.wrap.hidden = true; return; }

      colourEls.wrap.hidden = false;
      var chosen = model.colour || null;
      colourEls.value.textContent = chosen ? colourLabel(chosen) : T('team_pick', 'Let the team pick');

      colourEls.list.innerHTML =
        '<li><button type="button" class="sfsl__swatch sfsl__swatch--any' +
          (chosen ? '' : ' is-on') + '" data-colour="" title="' + esc(T('team_pick', 'Let the team pick')) + '" ' +
          'aria-label="' + esc(T('team_pick', 'Let the team pick')) + '"></button></li>' +
        COLOURS.map(function (c) {
          return '<li><button type="button" class="sfsl__swatch' +
            (chosen === c.name ? ' is-on' : '') + '" data-colour="' + esc(c.name) +
            '" title="' + esc(colourLabel(c.name)) + '" aria-label="' + esc(colourLabel(c.name)) +
            '" style="background:' + c.hex + '"></button></li>';
        }).join('');
    }

    if (colourEls.list) {
      colourEls.list.addEventListener('click', function (e) {
        var b = e.target.closest('[data-colour]');
        if (!b || !model) return;
        model.colour = b.getAttribute('data-colour') || null;
        renderColours();
        recompute();
      });
    }

    /* ----------------------------------------------------------- pricing */

    /*
     * Each part is priced on its own and the order is the sum, in a single
     * request. The figures come from the shop's 3D-Pi rate card, so they never
     * exist in this file — only the numbers it sends and receives.
     */
    var priceSeq = 0;
    var priceTimer = null;
    var prices = { items: [], total: null, lo: null, hi: null, atMin: false, currency: 'EGP' };

    function money(v) {
      if (typeof v !== 'number' || !isFinite(v)) return null;
      var cur = prices.currency === 'EGP' ? 'LE' : prices.currency;
      return cur + ' ' + Math.round(v).toLocaleString();
    }

    /* What a part COULD cost, never what it WILL. A single figure reads as a
       commitment; the band is the honest shape of a geometry estimate. When
       the floor price collapses the band, one figure is all there is to say. */
    function moneyRange(lo, hi, atMin) {
      // At the shop's floor price there is nothing to range over: the part
      // costs the minimum, and quoting "20–21" would invent a distinction.
      if (atMin) return money(lo);
      if (typeof lo !== 'number' || typeof hi !== 'number') return money(lo != null ? lo : hi);
      if (Math.round(lo) === Math.round(hi)) return money(lo);
      var cur = prices.currency === 'EGP' ? 'LE' : prices.currency;
      return cur + ' ' + Math.round(lo).toLocaleString() + '–' + Math.round(hi).toLocaleString();
    }

    function schedulePrices(files) {
      if (!cfg.priceUrl || !files.length) return;
      clearTimeout(priceTimer);
      var seq = ++priceSeq;
      priceTimer = setTimeout(function () { fetchPrices(files, seq); }, 350);
    }

    function fetchPrices(files, seq) {
      fetch(cfg.priceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: files.map(function (f) {
            return {
              grams: f.grams, minutes: f.minutes, quantity: f.quantity,
              gramsLo: f.gramsLo, gramsHi: f.gramsHi,
              minutesLo: f.minutesLo, minutesHi: f.minutesHi,
              material: f.materialPriceAs
            };
          })
        })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (seq !== priceSeq) return; // a newer request already answered
          if (!j || !j.ok) {
            prices = { items: [], total: null, lo: null, hi: null, atMin: false, currency: 'EGP' };
          } else {
            prices = {
              items: (j.items || []).map(function (i) {
                return { amount: i.amount, lo: i.lo, hi: i.hi, atMin: !!i.atMin };
              }),
              total: j.amount,
              lo: j.lo == null ? j.amount : j.lo,
              hi: j.hi == null ? j.amount : j.hi,
              atMin: !!j.atMin,
              currency: j.currency || 'EGP'
            };
          }
          refreshFiles();
          root.dispatchEvent(new CustomEvent('sfsl:price', {
            detail: prices.total === null ? null : prices, bubbles: true
          }));
        })
        .catch(function () {});
    }

    /* ------------------------------------------------------- the file list */

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    /*
     * The list is built once per structural change and only its text is
     * refreshed afterwards. Rebuilding innerHTML on every slider event would
     * destroy the slider mid-drag — and the settings now live inside the
     * selected row, so that matters.
     */
    var rows = {};
    var partSettings = q('[data-sfsl-part-settings]');

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function buildFiles() {
      if (!els.files || !els.filesList) return;
      els.files.hidden = models.length === 0;
      rows = {};

      /* The settings block lives inside whichever row is selected, so it has
         to be lifted out before the list is emptied — clearing innerHTML with
         it still attached destroys it, and the sliders never come back. */
      if (partSettings && partSettings.parentNode) {
        partSettings.parentNode.removeChild(partSettings);
      }

      els.filesList.innerHTML = '';
      if (partSettings) partSettings.hidden = models.length === 0 || !settingsOpen;
      if (els.filesHint) els.filesHint.hidden = models.length === 0 || settingsOpen;
      if (!models.length) return;

      models.forEach(function (m) {
        var li = el('li', 'sfsl__file');
        li.setAttribute('data-id', m.id);

        var head = el('div', 'sfsl__file-head');
        var pick = el('button', 'sfsl__file-pick');
        pick.type = 'button';
        pick.setAttribute('data-pick', m.id);
        var name = el('span', 'sfsl__file-name', m.name);
        var meta = el('span', 'sfsl__file-meta');
        var caret = el('span', 'sfsl__file-caret');
        caret.setAttribute('aria-hidden', 'true');
        caret.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" ' +
          'stroke-linecap="round" stroke-linejoin="round"><path d="m4 6 4 4 4-4"/></svg>';
        var text = el('span', 'sfsl__file-text');
        text.appendChild(name);
        text.appendChild(meta);
        pick.appendChild(caret);
        pick.appendChild(text);

        var price = el('span', 'sfsl__file-price');

        var qty = el('span', 'sfsl__file-qty');
        var minus = el('button', null, '−');
        minus.type = 'button';
        minus.setAttribute('data-step', '-1');
        minus.setAttribute('data-id', m.id);
        minus.setAttribute('aria-label', 'One fewer of ' + m.name);

        // Typed as well as stepped — nobody wants to press + forty times.
        var count = document.createElement('input');
        count.type = 'text';
        count.inputMode = 'numeric';
        count.className = 'sfsl__file-count';
        count.setAttribute('aria-label', 'How many of ' + m.name);
        count.setAttribute('data-qty', m.id);

        var plus = el('button', null, '+');
        plus.type = 'button';
        plus.setAttribute('data-step', '1');
        plus.setAttribute('data-id', m.id);
        plus.setAttribute('aria-label', 'One more of ' + m.name);
        qty.appendChild(minus);
        qty.appendChild(count);
        qty.appendChild(plus);

        var remove = el('button', 'sfsl__file-remove', '×');
        remove.type = 'button';
        remove.setAttribute('data-remove', m.id);
        remove.setAttribute('aria-label', 'Remove ' + m.name);

        head.appendChild(pick);
        head.appendChild(price);
        head.appendChild(qty);
        head.appendChild(remove);
        li.appendChild(head);

        /* What this part is set to, once its settings are folded away. Closing
           a panel should not hide the answers it holds. */
        var chips = el('div', 'sfsl__file-chips');
        li.appendChild(chips);

        // The one settings block follows the selection from row to row, and
        // is only in the tree at all while it is open.
        var open = m.id === activeId && settingsOpen;
        pick.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && partSettings) {
          li.appendChild(partSettings);
          partSettings.hidden = false;
          // Copying one part's choices onto the others only means something
          // when there are others.
          if (controls.partBar) controls.partBar.hidden = models.length < 2;
        }

        els.filesList.appendChild(li);
        rows[m.id] = { li: li, meta: meta, price: price, count: count, chips: chips };
      });

      refreshFiles();
    }

    /** Text only — safe to call on every keystroke and slider move. */
    function refreshFiles() {
      if (!models.length) return;
      var pieces = models.reduce(function (n, m) { return n + Math.max(1, m.qty || 1); }, 0);
      setText(
        els.filesCount,
        models.length === 1 ? T('one_file', '1 file')
          : models.length + ' ' + T('n_files', 'files') + ' · ' + pieces + ' ' + T('n_pieces', 'pieces')
      );

      models.forEach(function (m, i) {
        var r = rows[m.id];
        if (!r) return;
        /* Just enough to tell one part from another: its size, and a warning
           if it matters. Material, strength and colour are on screen already
           whenever the row is open. */
        var dims = m.est ? fmtMm(m.est.dims[0]) + ' × ' + fmtMm(m.est.dims[1]) +
          ' × ' + fmtMm(m.est.dims[2]) + ' mm' : '';
        var scaled = (m.scale || 100) !== 100 ? ' · ' + T('scaled', 'scaled') + ' ' + (m.scale || 100) + '%' : '';
        r.meta.textContent = dims + scaled +
          (m.fits === false ? ' · ' + T('needs_split', 'needs splitting') : '');
        var pi = prices.items[i];
        r.price.textContent = (pi ? moneyRange(pi.lo, pi.hi, pi.atMin) : '') || '';
        if (document.activeElement !== r.count) r.count.value = Math.max(1, m.qty || 1);

        var open = m.id === activeId && settingsOpen;
        r.li.classList.toggle('is-active', m.id === activeId);
        r.li.classList.toggle('is-open', open);
        r.li.classList.toggle('is-oversize', m.fits === false);

        /* Only while closed — with the panel open these would just repeat the
           controls sitting directly underneath. */
        if (r.chips) {
          r.chips.hidden = open;
          if (!open) {
            r.chips.innerHTML = '';
            var swatch = COLOURS.filter(function (c) { return c.name === m.colour; })[0];
            [
              { text: matOf(m).label },
              { text: strengthLabel(stepIndex(m)) },
              { text: m.colour ? colourLabel(m.colour) : T('colour_by_team', 'Colour by the team'), dot: swatch ? swatch.hex : null }
            ].forEach(function (c) {
              var chip = el('span', 'sfsl__chip-mini');
              if (c.dot) {
                var dot = el('i', 'sfsl__chip-dot');
                dot.style.background = c.dot;
                chip.appendChild(dot);
              }
              chip.appendChild(document.createTextNode(c.text));
              r.chips.appendChild(chip);
            });
          }
        }
      });
    }

    /*
     * `how` says what should happen to the settings panel:
     *   'toggle' (default) — a click on a row: same row folds it away
     *   'open'             — force it open (nothing does this today)
     *   'closed'           — a part just arrived: show it, but stay folded
     *   'keep'             — bookkeeping (a delete); leave the panel as it was
     */
    function setActive(id, how) {
      var m = models.filter(function (x) { return x.id === id; })[0];
      if (!m) return;
      if (view) view.setIdle(false);

      if (how === 'open') settingsOpen = true;
      else if (how === 'closed') settingsOpen = false;
      else if (how === 'keep') { /* leave settingsOpen alone */ }
      else settingsOpen = id !== activeId || !settingsOpen;

      activeId = id;
      model = m;
      setText(els.fname, m.name);
      setText(els.tri, m.count.toLocaleString() + ' triangles');
      if (view) view.load(m.pos, m.count, m.meas);
      syncControls();
      buildFiles();
      renderColours();
    }

    function removeModel(id) {
      models = models.filter(function (m) { return m.id !== id; });
      if (!models.length) return reset();

      // Removing a row changes the list's structure, so it always needs a
      // rebuild — refreshing text alone left the deleted row on screen.
      if (activeId === id) {
        setActive(models[0].id, 'keep'); // rebuilds on its way through
      } else {
        buildFiles();
      }
      recompute();
    }

    function stepQty(id, by) {
      var m = models.filter(function (x) { return x.id === id; })[0];
      if (!m) return;
      m.qty = Math.max(1, Math.min(999, (m.qty || 1) + by));
      recompute();
    }

    function fail(message) {
      if (els.fit) {
        els.fit.hidden = false;
        els.fit.className = 'sfsl__status sfsl__status--warn';
      }
      if (els.fitTxt) els.fitTxt.innerHTML = message;
    }

    function reset() {
      models = [];
      model = null;
      activeId = null;
      orderRef = null;
      if (view) showIdle();
      if (els.drop) els.drop.classList.remove('is-hidden');
      if (els.tools) els.tools.hidden = true;
      if (els.hud) els.hud.hidden = true;
      if (els.results) els.results.hidden = true;
      if (els.fit) els.fit.hidden = true;
      if (els.files) els.files.hidden = true;
      if (els.filesList) els.filesList.innerHTML = '';
      rows = {};
      settingsOpen = false;
      if (partSettings) partSettings.hidden = true;
      if (els.filesHint) els.filesHint.hidden = true;
      photos.forEach(function (p) { URL.revokeObjectURL(p.url); });
      photos = [];
      renderPhotos();
      prices = { items: [], total: null, lo: null, hi: null, atMin: false, currency: 'EGP' };
      setText(els.fname, '');
      [els.matname, els.dims].forEach(function (el) {
        if (!el) return;
        el.classList.add('sfsl__value--empty');
        el.textContent = '—';
      });
      setText(els.matsub, '');
      setText(els.vol, '');
      if (els.input) els.input.value = '';
      if (els.summary) els.summary.value = '';
      root.dispatchEvent(new CustomEvent('sfsl:estimate', { detail: null, bubbles: true }));
    }

    /** Load a list of files one after another, so a multi-select just works. */
    function loadAll(list, done) {
      var files = Array.prototype.slice.call(list || []);
      if (!files.length) return done && done();
      var room = MAX_FILES - models.length;
      if (room <= 0) {
        fail('<strong>That is as many files as one order can hold (' + MAX_FILES + ').</strong> ' +
             'Send the rest on WhatsApp and the team will add them.');
        return done && done();
      }
      if (files.length > room) {
        files = files.slice(0, room);
        fail('<strong>Only the first ' + room + ' were added</strong> — an order holds ' +
             MAX_FILES + ' files. Send the rest on WhatsApp.');
      }
      (function next(i) {
        if (i >= files.length) return done && done();
        load(files[i], function () { next(i + 1); });
      })(0);
    }

    function load(file, done) {
      if (!file) return done && done();
      if (!/\.stl$/i.test(file.name)) {
        fail('<strong>' + esc(file.name) + ' is not an STL.</strong> Export it as STL and drop it in ' +
             'again — or send it on WhatsApp and the team will handle any format.');
        return done && done();
      }
      if (file.size > MAX_BYTES) {
        fail('<strong>' + esc(file.name) + ' is over 40 MB.</strong> Send it on WhatsApp instead and ' +
             'the team will slice it on our machines.');
        return done && done();
      }
      if (models.some(function (m) { return m.name === file.name && m.size === file.size; })) {
        return done && done(); // already in the order; adding it twice is never meant
      }
      if (els.stage) els.stage.classList.add('is-working');

      var fr = new FileReader();
      fr.onerror = function () {
        if (els.stage) els.stage.classList.remove('is-working');
        fail('<strong>' + esc(file.name) + ' could not be read.</strong> Try exporting it again.');
        if (done) done();
      };
      fr.onload = function () {
        var parsed = null;
        try { parsed = parseStl(fr.result); } catch (e) { parsed = null; }
        if (els.stage) els.stage.classList.remove('is-working');

        if (!parsed || !parsed.count) {
          fail('<strong>' + esc(file.name) + ' could not be read as an STL.</strong> Re-export it as ' +
               'binary or ASCII STL, or send it on WhatsApp and the team will take a look.');
          return done && done();
        }
        var meas = measure(parsed.pos, parsed.count);
        if (!isFinite(meas.volumeMm3) || !meas.dims.every(function (d) { return isFinite(d) && d >= 0; })) {
          fail('<strong>' + esc(file.name) + ' does not measure cleanly.</strong> Send it on WhatsApp ' +
               'and the team will check it.');
          return done && done();
        }

        if (!orderRef) orderRef = makeRef(); // one reference for the whole order
        var entry = {
          id: nextId++,
          name: file.name,
          size: file.size,
          qty: 1,
          strength: STRENGTH_DEFAULT,
          scale: 100,
          mat: readMaterial(),
          meas: meas,
          count: parsed.count,
          pos: parsed.pos,
          file: file
        };
        models.push(entry);

        if (els.drop) els.drop.classList.add('is-hidden');
        if (els.tools) els.tools.hidden = false;
        if (els.hud) els.hud.hidden = false;
        /* A new part is shown in the viewport but its settings stay folded:
           dropping six files should leave six scannable rows, not a panel
           thrown open on whichever one happened to land last. */
        setActive(entry.id, 'closed');
        recompute();
        if (els.results && cfg.layout !== 'band' && models.length === 1) {
          els.results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        if (done) done();
      };
      fr.readAsArrayBuffer(file);
    }

    /* wiring */
    if (els.browse && els.input) {
      els.browse.addEventListener('click', function () { els.input.click(); });
    }

    /* The whole panel opens the picker, not just the button — it is a big,
       obvious target and people aim at the box. The button's own handler
       covers the click when they aim at it exactly. */
    if (els.drop && els.input) {
      els.drop.addEventListener('click', function (e) {
        if (e.target.closest('[data-sfsl-browse]')) return;
        els.input.click();
      });
    }
    if (els.input) {
      els.input.addEventListener('change', function (e) {
        loadAll(e.target.files);
        e.target.value = ''; // so the same file can be re-added after removal
      });
    }
    if (els.add && els.input) {
      els.add.addEventListener('click', function () { els.input.click(); });
    }

    /* One listener for the whole list — rows are rebuilt on every change. */
    if (els.filesList) {
      els.filesList.addEventListener('input', function (e) {
        var box = e.target.closest('[data-qty]');
        if (!box) return;
        var m = models.filter(function (x) { return x.id === +box.getAttribute('data-qty'); })[0];
        if (!m) return;
        var n = parseInt(String(box.value).replace(/\D/g, ''), 10);
        m.qty = Math.max(1, Math.min(999, isNaN(n) ? 1 : n));
        recompute();
      });

      // Tidy the box up once they leave it, so it never sits empty or absurd.
      els.filesList.addEventListener('blur', function (e) {
        var box = e.target.closest('[data-qty]');
        if (!box) return;
        var m = models.filter(function (x) { return x.id === +box.getAttribute('data-qty'); })[0];
        if (m) box.value = Math.max(1, m.qty || 1);
      }, true);

      els.filesList.addEventListener('click', function (e) {
        var t = e.target.closest('[data-pick], [data-step], [data-remove]');
        if (!t) return;
        if (t.hasAttribute('data-remove')) return removeModel(+t.getAttribute('data-remove'));
        if (t.hasAttribute('data-step')) {
          return stepQty(+t.getAttribute('data-id'), +t.getAttribute('data-step'));
        }
        setActive(+t.getAttribute('data-pick'));
      });
    }
    if (els.stage) {
      ['dragenter', 'dragover'].forEach(function (t) {
        els.stage.addEventListener(t, function (e) {
          e.preventDefault();
          if (els.drop) els.drop.classList.add('is-over');
        });
      });
      ['dragleave', 'drop'].forEach(function (t) {
        els.stage.addEventListener(t, function (e) {
          e.preventDefault();
          if (els.drop) els.drop.classList.remove('is-over');
        });
      });
      els.stage.addEventListener('drop', function (e) {
        loadAll(e.dataTransfer && e.dataTransfer.files);
      });
    }
    if (els.reset && view) els.reset.addEventListener('click', function () { view.reset(); });
    if (els.clear) els.clear.addEventListener('click', reset);

    ['mat', 'strength', 'scale'].forEach(function (k) {
      var el = controls[k];
      if (!el) return;
      var handler = function () {
        captureControls(); // the sliders edit the selected part, not the order
        recompute();
      };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });

    /*
     * Copy the open part's material, strength and colour onto every other
     * part. Scale is deliberately left alone: it is the one setting that is
     * about this specific model's size, and forcing it onto the others would
     * silently resize parts the customer never touched.
     */
    if (controls.applyAll) {
      controls.applyAll.addEventListener('click', function () {
        if (!model || models.length < 2) return;
        var src = model;
        models.forEach(function (m) {
          if (m === src) return;
          m.mat = src.mat;
          m.strength = src.strength;
          m.colour = src.colour;
        });
        recompute();
        buildFiles();
        renderColours();

        // Say it worked, then get out of the way.
        var b = controls.applyAll;
        var was = b.textContent;
        b.textContent = T('applied_all_a', 'Applied to all') + ' ' + models.length + ' ' + T('applied_all_b', 'parts');
        b.classList.add('is-done');
        setTimeout(function () {
          b.textContent = was;
          b.classList.remove('is-done');
        }, 1800);
      });
    }

    /*
     * Typing a scale. Clamped to the slider's own range so the two can never
     * disagree, and only applied on a complete value — rejecting "1" while
     * someone is on their way to "130" would make the box unusable.
     */
    if (controls.scaleV) {
      var applyTyped = function (commit) {
        var raw = String(controls.scaleV.value).replace(/[^0-9]/g, '');
        if (!raw) { if (commit) recompute(); return; }
        var min = parseInt(controls.scale.min, 10) || 10;
        var max = parseInt(controls.scale.max, 10) || 1000;
        var v = Math.max(min, Math.min(max, parseInt(raw, 10)));
        if (!model) return;
        model.scale = v;
        if (controls.scale) controls.scale.value = String(v);
        if (commit) controls.scaleV.value = String(v);
        recompute();
      };
      controls.scaleV.addEventListener('input', function () { applyTyped(false); });
      controls.scaleV.addEventListener('blur', function () { applyTyped(true); });
      controls.scaleV.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyTyped(true); controls.scaleV.blur(); }
      });
      // The box lives inside the row's disclosure button; a click in it must
      // not fold the panel away.
      controls.scaleV.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    if (controls.scaleReset) {
      controls.scaleReset.addEventListener('click', function () {
        if (!model) return;
        model.scale = 100;
        if (controls.scale) controls.scale.value = '100';
        if (controls.scaleV) controls.scaleV.value = '100';
        recompute();
      });
    }

    if (photoEls.add && photoEls.input) {
      photoEls.add.addEventListener('click', function () { photoEls.input.click(); });
    }
    if (photoEls.input) {
      photoEls.input.addEventListener('change', function (e) {
        addPhotos(e.target.files);
        e.target.value = '';
      });
    }
    if (photoEls.list) {
      photoEls.list.addEventListener('click', function (e) {
        var t = e.target.closest('[data-photo-remove]');
        if (t) removePhoto(+t.getAttribute('data-photo-remove'));
      });
    }

    recompute();
    root.sfSlicer = {
      reset: reset,
      // The guided flow needs the bytes themselves to upload on submit.
      getFile: function () { return model ? model.file : null; },
      getFiles: function () { return models.map(function (m) { return m.file; }); },
      getPhotos: function () { return photos.map(function (p) { return p.file; }); },

      /* Restoring a draft after a page refresh. The files come back as real
         File objects and go through the same path as a drop, so there is no
         second way to load a model that could drift from the first. */
      addFiles: function (list, done) { loadAll(list, done); },
      addPhotos: function (list) { addPhotos(list); },

      /* What each part was set to. Saved by index, because the files are
         restored in the order they were added. */
      getSettings: function () {
        return models.map(function (m) {
          return {
            mat: matOf(m).label,
            strength: stepIndex(m),
            scale: m.scale || 100,
            colour: m.colour || null,
            qty: Math.max(1, m.qty || 1)
          };
        });
      },
      applySettings: function (list) {
        if (!list || !list.length) return;
        models.forEach(function (m, i) {
          var v = list[i];
          if (!v) return;
          if (typeof v.strength === 'number') m.strength = v.strength;
          if (typeof v.scale === 'number') m.scale = v.scale;
          if (typeof v.qty === 'number') m.qty = v.qty;
          m.colour = v.colour || null;
          // Materials are matched by their label: the density on the option is
          // the value, and it is not unique across the list.
          if (v.mat && controls.mat) {
            for (var oi = 0; oi < controls.mat.options.length; oi++) {
              var o = controls.mat.options[oi];
              if (o.textContent.trim() === v.mat) {
                m.mat = {
                  label: o.textContent.trim(),
                  density: parseFloat(o.value) || 1.24,
                  speed: parseFloat(o.getAttribute('data-speed')) || 1,
                  priceAs: o.getAttribute('data-price-as') || o.textContent.trim()
                };
                break;
              }
            }
          }
        });
        syncControls();
        recompute();
        buildFiles();
        renderColours();
      },
      destroy: function () { if (view) view.destroy(); }
    };
  }

  /* --------------------------------------------------------------- upgrade */

  function upgradeAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sf-slicer]');
    for (var i = 0; i < nodes.length; i++) Slicer(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { upgradeAll(); });
  } else {
    upgradeAll();
  }

  // Theme editor: sections are torn down and re-inserted on every edit.
  document.addEventListener('shopify:section:load', function (e) { upgradeAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var nodes = e.target.querySelectorAll('[data-sf-slicer]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].sfSlicer) nodes[i].sfSlicer.destroy();
    }
  });
})();
